import React from 'react';

import EasyPayOption from '../easy-pay-option/easy-pay-option';

import {
  useDTCCApplication,
  useDTCCFinancingPromotion,
  useMessage,
  useModal,
  useMyAccount,
  useNavigateTo
} from '../../shared/hooks/hooks';

import { trackAction } from '../../shared/utils/analytics/analytics';
import { interpolateCmsData } from '../../shared/utils/cms/cms';
import { getIsPromotional } from '../../shared/utils/dtcc-banner/dtcc-banner';
import { convertIntPlanDuration } from '../../shared/utils/payment-utils/payment-utils';

import { Maybe } from '../../shared/types/graphql-types';
import { MapPrice } from '../../shared/types/product';
import { DTCC_APPLICATION_STATUS } from '../../shared/constants/cart';
import {
  IMAGE_KEYS,
  MONTHLY_MESSAGING_KEYS,
  PROMOTIONAL_MESSAGING_KEYS
} from '../../shared/constants/dtcc-banner-constants';
import { ONE_CLICK_MODAL } from '../../shared/constants/modals';
import { PageTypes } from '../../shared/constants/page-types';

interface DTCCBannerProps {
  applyNowButtonId?: string;
  className?: string;
  combinedMapPrice?: MapPrice;
  formattedValue?: Maybe<string>;
  hideImage?: boolean;
  hideLink?: boolean;
  linkText?: string;
  pageType?: keyof typeof PageTypes;
  setShowPromoTitle?: (show) => void;
  shouldOpenLinkInNewTab?: boolean;
  showImageAfterMessage?: boolean;
  showLinkAsCTA?: boolean;
  value?: number;
}

function getMonthlyPayment(cost, planDuration) {
  return Math.ceil((cost || 0) / (planDuration || 0));
}

function DTCCBanner(props: DTCCBannerProps) {
  const {
    applyNowButtonId,
    className = '',
    combinedMapPrice = null,
    formattedValue,
    hideImage = false,
    hideLink = false,
    linkText: linkTextProp,
    pageType,
    setShowPromoTitle,
    shouldOpenLinkInNewTab = false,
    showImageAfterMessage,
    showLinkAsCTA,
    value
  } = props;
  const { isFleetAccount } = useMyAccount();
  const { openModal } = useModal();
  const { dtccApplicationStatus, isClientLoaded } =
    useDTCCApplication(pageType);
  const navigateTo = useNavigateTo();

  const { promotionData: planData, promotionTermsCmsId } =
    useDTCCFinancingPromotion(value);

  const planDuration = convertIntPlanDuration(planData?.planDuration);
  const monthlyPayment = getMonthlyPayment(value, planDuration);
  const isPromotional = getIsPromotional(combinedMapPrice, planDuration);
  const textContentKeys = isPromotional
    ? PROMOTIONAL_MESSAGING_KEYS
    : MONTHLY_MESSAGING_KEYS;
  const imageContentKeys = window.isDT ? IMAGE_KEYS.dt : IMAGE_KEYS.at;
  const messages = useMessage(textContentKeys.concat(imageContentKeys)) || {};
  const linkURL = messages.DTCC_PROMO_MORE_DETAILS_LINK_URL;

  if (
    (isFleetAccount && process.env.__FF_FLEET_ACCOUNT__ === 'true') ||
    !isClientLoaded ||
    (!planData && !isPromotional) ||
    dtccApplicationStatus === DTCC_APPLICATION_STATUS.INSUFFICIENT
  ) {
    return null;
  }

  function handleOnClick() {
    trackAction(
      isPromotional
        ? 'dtcc_cta_apply_promotional_learnmore'
        : 'dtcc_cta_apply_monthly_learnhow'
    );
    if (isPromotional) {
      !shouldOpenLinkInNewTab
        ? navigateTo(linkURL)
        : window.open(linkURL, '_blank');
    } else {
      openModal(ONE_CLICK_MODAL, {
        applyNowButtonId,
        formattedValue,
        monthlyPayment,
        pageType,
        planDuration,
        promotionTermsCmsId
      });
    }
  }

  const imageAltText =
    messages.DTCC_PROMO_IMAGE_DISCOUNTTIRE_ALTTEXT ??
    messages.DTCC_PROMO_IMAGE_AMERICASTIRE_ALTTEXT;
  const imageURL =
    messages.DTCC_PROMO_IMAGE_DISCOUNTTIRE_URL ??
    messages.DTCC_PROMO_IMAGE_AMERICASTIRE_URL;
  let linkText = combinedMapPrice
    ? messages.DTCC_PROMO_MODAL_TRIGGER_MESSAGE ??
      messages.DTCC_PROMO_MORE_DETAILS_LINK_MESSAGE
    : linkTextProp;

  if (!linkText) {
    linkText = planDuration ? 'learn how' : 'learn more';
  }

  function getPromotionalText() {
    let promotionalText;

    if (messages.DTCC_PROMO_FINANCING_DETAILS) {
      promotionalText = interpolateCmsData(
        {
          financingPeriod: planDuration,
          monthlyPrice: monthlyPayment
        },
        messages.DTCC_PROMO_FINANCING_DETAILS
      );
    } else {
      promotionalText = messages.DTCC_PROMO_FINANCING_AVAILABLE;
      setShowPromoTitle && setShowPromoTitle(false);
    }

    return promotionalText;
  }

  return (
    <EasyPayOption
      className={`dtcc-apply-now ${className} ${
        isPromotional ? 'dtcc-apply-now--promotional' : ''
      }`}
      imageAlt={imageAltText}
      imageUrl={hideImage ? undefined : imageURL}
      linkText={hideLink ? undefined : linkText}
      message={
        <span
          dangerouslySetInnerHTML={{
            __html: getPromotionalText()
          }}
        />
      }
      onClick={handleOnClick}
      showImageAfterMessage={showImageAfterMessage}
      showLinkAsCTA={showLinkAsCTA}
    />
  );
}

export default DTCCBanner;
